import React, { useLayoutEffect, useRef, useState } from "react";
import CosmosStats from "./CosmosStats";
import { createPaymentEcosystemScene } from "./paymentEcosystemScene";
import { getCaptecPerformanceProfile } from "./scrollPerformance";

const WHITE_LOGO_SRC = "/assets/logos/captec-logo-white.png";
const WALLET_ICON_SRC = "/assets/immersive/captec-wallet-icon.png";
const SESSION_KEY = "captec-immersive-hero-played-v17";

// The white animation asset is a wide wordmark, while the final full-color
// asset includes a second-line descriptor. These calibrated ratios align the
// visible primary Captec wordmark rather than the transparent image canvases.
const FINAL_WHITE_WIDTH_RATIO = 0.886;
const FINAL_WHITE_X_OFFSET_RATIO = 0.006;
const FINAL_WHITE_Y_OFFSET_RATIO = -0.089;

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const segment = (progress, start, end) =>
  clamp01((progress - start) / Math.max(0.0001, end - start));
const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
const easeInOutCubic = (value) =>
  value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;

const getSessionPlayed = () => {
  if (typeof window === "undefined") return false;

  try {
    return window.sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export default function Hero() {
  const sectionRef = useRef(null);
  const sceneHostRef = useRef(null);
  const logoEmergenceRef = useRef(null);
  const introWalletRef = useRef(null);
  const stageLogoMaskRef = useRef(null);
  const stageLogoRef = useRef(null);
  const finalContentRef = useRef(null);
  const finalLogoTargetRef = useRef(null);
  const progressBarRef = useRef(null);
  const completionRef = useRef(false);
  const wordRefs = useRef([]);

  const [initiallyComplete] = useState(() => getSessionPlayed());
  const [countersActive, setCountersActive] = useState(initiallyComplete);
  const countersStartedRef = useRef(initiallyComplete);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const sceneHost = sceneHostRef.current;
    const logoEmergence = logoEmergenceRef.current;
    const introWallet = introWalletRef.current;
    const stageLogoMask = stageLogoMaskRef.current;
    const stageLogo = stageLogoRef.current;
    const finalContent = finalContentRef.current;
    const finalLogoTarget = finalLogoTargetRef.current;
    const progressBar = progressBarRef.current;
    const finalColorLogo = finalLogoTarget?.querySelector(
      ".cosmos-hero-color-logo"
    );

    if (
      !section ||
      !sceneHost ||
      !logoEmergence ||
      !introWallet ||
      !stageLogoMask ||
      !stageLogo ||
      !finalContent ||
      !finalLogoTarget
    ) {
      return undefined;
    }

    const profile = getCaptecPerformanceProfile();
    const { lowResource, reducedMotion } = profile;
    if (finalColorLogo) finalColorLogo.style.opacity = initiallyComplete ? "1" : "0";
    // Compact/touch layouts use the same complete scene without a scroll trap.
    const compactScreen = window.matchMedia("(max-width: 900px), (max-height: 580px)");
    const shouldSkip = initiallyComplete || reducedMotion || compactScreen.matches;
    const finalRevealElements = Array.from(
      section.querySelectorAll("[data-immersive-final]")
    );

    let scrollRaf = 0;
    let pointerRaf = 0;
    let maxProgress = shouldSkip ? 1 : 0;
    let lastRenderedProgress = -1;
    let scene = null;
    let sectionTop = 0;
    let runway = 1;
    let sectionEnd = 1;
    let pinState = "";
    let pointerX = 0;
    let pointerY = 0;
    let finalLogoDx = 0;
    let finalLogoDy = 0;
    let finalLogoScale = 0.44;

    const measureFinalLogoTarget = () => {
      const targetRect = finalLogoTarget.getBoundingClientRect();
      const stageRect = section
        .querySelector(".immersive-hero-stage")
        ?.getBoundingClientRect();
      const lockupWidth = Math.max(1, logoEmergence.offsetWidth);
      const stageCenterX = stageRect
        ? stageRect.left + stageRect.width / 2
        : window.innerWidth / 2;
      const stageCenterY = stageRect
        ? stageRect.top + stageRect.height / 2
        : window.innerHeight / 2;
      const targetCenterX =
        targetRect.left +
        targetRect.width / 2 +
        targetRect.width * FINAL_WHITE_X_OFFSET_RATIO;
      const targetCenterY =
        targetRect.top +
        targetRect.height / 2 +
        targetRect.height * FINAL_WHITE_Y_OFFSET_RATIO;

      finalLogoDx = targetCenterX - stageCenterX;
      finalLogoDy = targetCenterY - stageCenterY;
      finalLogoScale = Math.max(
        0.18,
        (targetRect.width * FINAL_WHITE_WIDTH_RATIO) / lockupWidth
      );
    };

    const placeLogoAtFinalTarget = () => {
      measureFinalLogoTarget();

      const lockupWidth = Math.max(1, logoEmergence.offsetWidth);
      logoEmergence.style.opacity = "0";
      logoEmergence.style.transform = `translate3d(calc(-50% + ${finalLogoDx.toFixed(
        2
      )}px), calc(-50% + ${finalLogoDy.toFixed(
        2
      )}px), 0) scale(${finalLogoScale.toFixed(4)})`;

      stageLogoMask.style.opacity = "1";
      stageLogoMask.style.left = "0px";
      stageLogoMask.style.width = `${lockupWidth.toFixed(2)}px`;

      stageLogo.style.opacity = "1";
      stageLogo.style.left = "0px";
      stageLogo.style.top = "0px";
      stageLogo.style.width = `${lockupWidth.toFixed(2)}px`;
      stageLogo.style.height = "auto";
      stageLogo.style.transform = "translate3d(0, 0, 0) scale(1)";

      introWallet.style.opacity = "0";
      if (finalColorLogo) finalColorLogo.style.opacity = "1";
    };

    const setPinState = (nextState) => {
      if (pinState === nextState) return;
      pinState = nextState;
      section.classList.toggle("is-pinned", nextState === "pinned");
      section.classList.toggle("is-past", nextState === "past");
    };

    const measure = () => {
      sectionTop = section.getBoundingClientRect().top + window.scrollY;
      runway = Math.max(1, section.offsetHeight - window.innerHeight);
      sectionEnd = sectionTop + runway;
      measureFinalLogoTarget();
    };

    const setFinalState = () => {
      setPinState("");
      section.classList.add("is-complete");
      section.style.setProperty("--immersive-progress", "1");
      sceneHost.style.opacity = "0";
      placeLogoAtFinalTarget();
      introWallet.style.opacity = "0";
      finalContent.style.opacity = "1";
      finalContent.style.transform = "translate3d(0, 0, 0)";
      if (finalColorLogo) finalColorLogo.style.opacity = "1";

      wordRefs.current.forEach((word) => {
        if (!word) return;
        word.style.opacity = "1";
        word.style.transform = "translate3d(0, 0, 0)";
      });

      finalRevealElements.forEach((element) => {
        element.style.opacity = "1";
        element.style.transform = "translate3d(0, 0, 0)";
      });

      if (!countersStartedRef.current) {
        countersStartedRef.current = true;
        setCountersActive(true);
      }

      if (progressBar) progressBar.style.transform = "scaleX(1)";
    };

    if (shouldSkip) {
      completionRef.current = true;
      measure();
      setFinalState();

      const readyTimer = window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("captec:immersive-hero-complete", {
            detail: {
              targetY: sectionTop,
              heroReady: true,
              skipped: true,
            },
          })
        );
      }, 0);

      return () => window.clearTimeout(readyTimer);
    }

    try {
      scene = createPaymentEcosystemScene(sceneHost);
    } catch (error) {
      console.warn("Captec immersive hero fallback:", error);
      completionRef.current = true;
      setFinalState();

      const fallbackTimer = window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("captec:immersive-hero-complete", {
            detail: {
              targetY: sectionTop,
              heroReady: true,
              skipped: true,
              fallback: true,
            },
          })
        );
      }, 0);

      return () => window.clearTimeout(fallbackTimer);
    }

    const updateDom = (progress, force = false) => {
      const safeProgress = clamp01(progress);

      if (
        !force &&
        Math.abs(safeProgress - lastRenderedProgress) <
          (lowResource ? 0.0024 : 0.0012)
      ) {
        return;
      }

      lastRenderedProgress = safeProgress;
      section.style.setProperty(
        "--immersive-progress",
        safeProgress.toFixed(4)
      );

      /*
       * One continuous lockup: the colored wallet and the white Captec logo
       * travel together. The logo is revealed from behind the wallet with a
       * horizontal sweep, then the same logo moves into its final hero slot.
       */
      const walletTravel = easeInOutCubic(segment(safeProgress, 0.34, 0.62));
      const logoReveal = easeOutCubic(segment(safeProgress, 0.38, 0.66));
      const lockupToFinal = easeInOutCubic(segment(safeProgress, 0.66, 0.86));
      const whiteLogoFade = easeInOutCubic(segment(safeProgress, 0.84, 0.9));
      const finalColorLogoReveal = easeInOutCubic(
        segment(safeProgress, 0.9, 0.96)
      );
      const coloredWalletFade = easeInOutCubic(segment(safeProgress, 0.76, 0.88));
      const whiteIconReveal = easeInOutCubic(segment(safeProgress, 0.72, 0.86));

      const revealGroupX = -window.innerWidth * 0.045 * walletTravel;
      const revealGroupY = -window.innerHeight * 0.012 * walletTravel;
      const groupX = revealGroupX + (finalLogoDx - revealGroupX) * lockupToFinal;
      const groupY = revealGroupY + (finalLogoDy - revealGroupY) * lockupToFinal;
      const groupScale = 1 + (finalLogoScale - 1) * lockupToFinal;

      logoEmergence.style.opacity = String(1 - whiteLogoFade);
      if (finalColorLogo) {
        finalColorLogo.style.opacity = String(finalColorLogoReveal);
      }
      logoEmergence.style.transform = `translate3d(calc(-50% + ${groupX.toFixed(2)}px), calc(-50% + ${groupY.toFixed(2)}px), 0) scale(${groupScale.toFixed(4)})`;

      /*
       * The left edge remains masked while the colored wallet covers the
       * original white wallet icon. The right edge opens progressively, so
       * the wordmark looks as if it is being pulled out of the colored wallet.
       * As the colored wallet disappears, the mask also opens to reveal the
       * original white icon without introducing a second logo.
       */
      const fixedLeftMask = 17.8 * (1 - whiteIconReveal);
      const revealedRightEdge =
        fixedLeftMask + logoReveal * (100 - fixedLeftMask);
      const lockupWidth = Math.max(1, logoEmergence.offsetWidth);
      const maskLeftPx = lockupWidth * (fixedLeftMask / 100);
      const revealRightPx = lockupWidth * (revealedRightEdge / 100);
      const maskWidthPx = Math.max(0, revealRightPx - maskLeftPx);

      stageLogoMask.style.opacity = logoReveal > 0.001 ? "1" : "0";
      stageLogoMask.style.left = `${maskLeftPx.toFixed(2)}px`;
      stageLogoMask.style.width = `${maskWidthPx.toFixed(2)}px`;

      stageLogo.style.opacity = "1";
      stageLogo.style.left = `${(-maskLeftPx).toFixed(2)}px`;
      stageLogo.style.width = `${lockupWidth.toFixed(2)}px`;
      stageLogo.style.transform = "translate3d(0, 0, 0) scale(1)";

      const coloredCardLeft = 50 + (6.9 - 50) * walletTravel;
      const coloredCardTop = 50 + (44 - 50) * walletTravel;
      const coloredCardScale = 1.28 - walletTravel * 0.28;
      const coloredCardRotation = -6 * (1 - walletTravel);

      introWallet.style.left = `${coloredCardLeft.toFixed(3)}%`;
      introWallet.style.top = `${coloredCardTop.toFixed(3)}%`;
      introWallet.style.opacity = String(Math.max(0, 1 - coloredWalletFade));
      introWallet.style.transform = `translate3d(-50%, -50%, 0) rotate(${coloredCardRotation.toFixed(2)}deg) scale(${coloredCardScale.toFixed(4)})`;

      const sceneFade = easeInOutCubic(segment(safeProgress, 0.46, 0.6));
      sceneHost.style.opacity = String(1 - sceneFade);

      const copyReveal = easeOutCubic(segment(safeProgress, 0.8, 0.95));

      if (safeProgress >= 0.82 && !countersStartedRef.current) {
        countersStartedRef.current = true;
        setCountersActive(true);
      }
      finalContent.style.opacity = String(copyReveal);
      finalContent.style.transform = `translate3d(0, ${(
        26 * (1 - copyReveal)
      ).toFixed(2)}px, 0)`;

      wordRefs.current.forEach((word, index) => {
        if (!word) return;
        const reveal = easeOutCubic(
          segment(safeProgress, 0.82 + index * 0.008, 0.92 + index * 0.008)
        );
        word.style.opacity = String(reveal);
        word.style.transform = `translate3d(0, ${(
          20 * (1 - reveal)
        ).toFixed(2)}px, 0)`;
      });

      finalRevealElements.forEach((element, index) => {
        const reveal = easeOutCubic(
          segment(safeProgress, 0.84 + index * 0.018, 0.97 + index * 0.018)
        );
        element.style.opacity = String(reveal);
        element.style.transform = `translate3d(0, ${(
          16 * (1 - reveal)
        ).toFixed(2)}px, 0)`;
      });

      if (progressBar) {
        progressBar.style.transform = `scaleX(${safeProgress})`;
      }

      scene?.update(safeProgress);
      scene?.setPointer(pointerX, pointerY);
    };

    const finishExperience = () => {
      if (completionRef.current) return;
      completionRef.current = true;

      updateDom(1, true);
      scene?.dispose();
      scene = null;

      try {
        window.sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        // Session storage is optional.
      }

      const previousScrollBehavior =
        document.documentElement.style.scrollBehavior;
      const previousBodyOverflowAnchor = document.body.style.overflowAnchor;
      const expandedHeight = section.offsetHeight;
      const compactHeight = Math.max(window.innerHeight, 680);
      const collapseDelta = Math.max(0, expandedHeight - compactHeight);
      const targetY = Math.max(sectionTop, window.scrollY - collapseDelta);

      /*
       * The stage stays fixed for the single frame in which the long runway is
       * collapsed. Scroll position is compensated by the exact removed height,
       * so the viewport does not jump even though the document becomes shorter.
       */
      section.classList.add("is-releasing");
      section.getBoundingClientRect();

      setPinState("");
      section.classList.add("is-complete");
      placeLogoAtFinalTarget();
      window.removeEventListener("scroll", requestProgressUpdate);

      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.overflowAnchor = "none";

      window.requestAnimationFrame(() => {
        window.scrollTo({ top: targetY, left: 0, behavior: "auto" });

        window.requestAnimationFrame(() => {
          section.classList.remove("is-releasing");
          document.documentElement.style.scrollBehavior =
            previousScrollBehavior;
          document.body.style.overflowAnchor = previousBodyOverflowAnchor;

          window.dispatchEvent(
            new CustomEvent("captec:immersive-hero-complete", {
              detail: {
                targetY,
                heroReady: true,
                skipped: false,
              },
            })
          );
        });
      });
    };

    const calculateProgress = () => {
      scrollRaf = 0;
      if (completionRef.current) return;

      const currentScroll = Math.max(0, window.scrollY);

      if (currentScroll < sectionTop) {
        setPinState("");
      } else if (currentScroll <= sectionEnd) {
        setPinState("pinned");
      } else {
        setPinState("past");
      }

      const rawProgress = clamp01((currentScroll - sectionTop) / runway);

      if (rawProgress > maxProgress) {
        maxProgress = rawProgress;
        updateDom(maxProgress);
      }

      if (maxProgress >= 0.997) finishExperience();
    };

    function requestProgressUpdate() {
      if (scrollRaf || completionRef.current) return;
      scrollRaf = window.requestAnimationFrame(calculateProgress);
    }

    const onResize = () => {
      measure();
      if (compactScreen.matches && !completionRef.current) {
        const previousHeight = section.offsetHeight;
        completionRef.current = true;
        scene?.dispose();
        scene = null;
        setFinalState();
        window.removeEventListener("scroll", requestProgressUpdate);
        if (window.scrollY > sectionTop) {
          window.scrollTo({ top: Math.max(sectionTop, window.scrollY - Math.max(0, previousHeight - section.offsetHeight)), behavior: "auto" });
        }
        window.dispatchEvent(new CustomEvent("captec:immersive-hero-complete", { detail: { heroReady: true, skipped: true } }));
        return;
      }
      scene?.resize();

      if (completionRef.current) {
        placeLogoAtFinalTarget();
        return;
      }

      requestProgressUpdate();
    };

    const applyPointer = () => {
      pointerRaf = 0;
      section.style.setProperty(
        "--pointer-x-px",
        `${(pointerX * 14).toFixed(2)}px`
      );
      section.style.setProperty(
        "--pointer-y-px",
        `${(pointerY * 10).toFixed(2)}px`
      );
      section.style.setProperty(
        "--pointer-x-reverse-px",
        `${(pointerX * -10).toFixed(2)}px`
      );
      section.style.setProperty(
        "--pointer-y-reverse-px",
        `${(pointerY * -7).toFixed(2)}px`
      );
      section.style.setProperty(
        "--pointer-x-half-px",
        `${(pointerX * 7).toFixed(2)}px`
      );
      section.style.setProperty(
        "--pointer-y-half-reverse-px",
        `${(pointerY * -5).toFixed(2)}px`
      );
      scene?.setPointer(pointerX, pointerY);
    };

    const onPointerMove = (event) => {
      pointerX = (event.clientX / window.innerWidth - 0.5) * 2;
      pointerY = (event.clientY / window.innerHeight - 0.5) * 2;

      if (!pointerRaf) {
        pointerRaf = window.requestAnimationFrame(applyPointer);
      }
    };

    measure();
    updateDom(0, true);
    requestProgressUpdate();

    window.addEventListener("scroll", requestProgressUpdate, {
      passive: true,
    });
    window.addEventListener("resize", onResize, { passive: true });
    if (!lowResource) {
      window.addEventListener("pointermove", onPointerMove, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", requestProgressUpdate);
      window.removeEventListener("resize", onResize);
      if (!lowResource) {
        window.removeEventListener("pointermove", onPointerMove);
      }
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
      if (pointerRaf) window.cancelAnimationFrame(pointerRaf);
      scene?.dispose();
    };
  }, [initiallyComplete]);

  return (
    <section
      ref={sectionRef}
      className={`hero-section immersive-hero ${
        initiallyComplete ? "is-complete" : ""
      }`}
      id="escala"
      aria-label="Escala que ya opera"
    >
      <div className="immersive-hero-stage">
        <div className="immersive-hero-grid" aria-hidden="true" />
        <div className="immersive-hero-aurora" aria-hidden="true" />
        <div className="immersive-hero-scene" ref={sceneHostRef} aria-hidden="true" />

        <div
          ref={logoEmergenceRef}
          className="immersive-logo-emergence"
        >
          <div
            ref={stageLogoMaskRef}
            className="immersive-stage-logo-mask"
            aria-hidden="true"
          >
            <img
              ref={stageLogoRef}
              className="immersive-stage-logo"
              src={WHITE_LOGO_SRC}
              alt="Captec"
              draggable={false}
            />
          </div>

          <img
            ref={introWalletRef}
            className="immersive-intro-wallet"
            src={WALLET_ICON_SRC}
            alt=""
            draggable={false}
          />
        </div>

        <div
          className="immersive-final-content immersive-cosmos-final"
          ref={finalContentRef}
        >
          <CosmosStats
            embedded
            countersActive={countersActive}
            logoTargetRef={finalLogoTargetRef}
          />
        </div>

        <div className="immersive-scroll-guide" aria-hidden="true">
          <span className="immersive-scroll-guide__label">
            Desliza para entrar
          </span>
          <span className="immersive-scroll-guide__track">
            <span ref={progressBarRef} />
          </span>
        </div>
      </div>
    </section>
  );
}
